import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { formatPrice } from '@spotahome/soyuz-currency';

import { getDownPaymentForFirstMonthStrategy } from '../utils/pricing';
import {
  COST_TYPE_DOWNPAYMENT,
  COST_TYPE_SAH_FEE
} from '../../constants/costs';

import { ListingCostsShape } from '../../constants/shapes';

import { formatHomecardDateFullMonthYear } from '../utils/dateUtils';

import { getUnit, isEstimatedCost } from './costHelpers';

import DetailRow from './DetailRow';

import styles from './OnBookingClosed.module.scss';

const getDownpaymentCopies = ({
  pricing,
  downPaymentTimeInterval,
  landlordContractType
}) => {
  const {
    downPaymentStrategy,
    tenantPaymentsWithCoverage = {},
    checkIn,
    checkOut
  } = pricing;
  const isDownPaymentWeekly = downPaymentTimeInterval?.type === 'weekly';
  const isDownPaymentFirstMonthStrategy =
    downPaymentStrategy === 'downPaymentFirstMonthStrategy';

  const downPaymentBaseItem = {
    name: isDownPaymentWeekly
      ? trans(
          `booking_request.spotahome_booking_payment_info.summary.week_advance`,
          {
            weeks: downPaymentTimeInterval?.value
          }
        )
      : trans('property_show.book_now_card.first_month'),
    tooltip: isDownPaymentWeekly
      ? trans(
          `booking_request.spotahome_booking_payment_info.week.in.advance.payment`,
          {
            weeks: downPaymentTimeInterval?.value
          }
        )
      : trans('property_show.book_now_card.tooltip.first_payment.summary')
  };

  return isDownPaymentFirstMonthStrategy
    ? {
        ...downPaymentBaseItem,
        ...getDownPaymentForFirstMonthStrategy({
          landlordContractType,
          tenantPaymentsWithCoverage,
          checkIn,
          checkOut
        }),
        from: tenantPaymentsWithCoverage?.downPaymentFrom,
        to: tenantPaymentsWithCoverage?.downPaymentTo
      }
    : downPaymentBaseItem;
};

const OnBookingClosed = ({
  currencyCode = null,
  pricing = {},
  costs = {},
  downPaymentTimeInterval = undefined,
  isInstantBooking = false
}) => {
  const totalValue = formatPrice(costs.total, currencyCode);

  const downpaymentCopies = getDownpaymentCopies({
    pricing,
    downPaymentTimeInterval,
    landlordContractType: pricing.landlordContractType
  });

  const downpaymentCost = costs?.costs?.find(
    c => c.type === COST_TYPE_DOWNPAYMENT
  );
  const feeCost = costs?.costs?.find(c => c.type === COST_TYPE_SAH_FEE);
  const restCosts = costs?.costs?.filter(
    c => c.type !== COST_TYPE_DOWNPAYMENT && c.type !== COST_TYPE_SAH_FEE
  );

  const downpaymentValue = downpaymentCost?.value;
  const feeValue = feeCost?.value;

  const downpaymentTooltipPrefix =
    downpaymentCopies.from && downpaymentCopies.to
      ? `${formatHomecardDateFullMonthYear(
          downpaymentCopies.from
        )} – ${formatHomecardDateFullMonthYear(downpaymentCopies.to)}`
      : null;

  const downpaymentTooltipText = downpaymentTooltipPrefix
    ? [downpaymentTooltipPrefix, downpaymentCopies.tooltip]
        .filter(part => part != null && part !== '')
        .join('\n\n')
    : downpaymentCopies.tooltip;

  return (
    <div
      className={styles['on-booking-closed-card']}
      data-test="spotahomeBookingPaymentInfo.total"
    >
      <header className={styles['on-booking-closed-card__header']}>
        <h3 className={styles['on-booking-closed-card__title']}>
          {isInstantBooking
            ? trans('payment.timeline.section1.title.instant-booking')
            : trans('payment.details.on_booking_closed.title')}
        </h3>
        <p className={styles['on-booking-closed-card__subtitle']}>
          {trans('payment.details.on_booking_closed.subtitle')}
        </p>
      </header>

      <div className={styles['on-booking-closed-card__rows']}>
        <DetailRow
          label={downpaymentCopies.name}
          tooltipText={downpaymentTooltipText}
          value={downpaymentValue}
          valueWithoutOffer={downpaymentCost?.valueWithoutOffer}
          currencyCode={currencyCode}
          dataTest="spotahomeBookingPaymentInfo.downPayment"
        />

        {feeValue ? (
          <DetailRow
            label={trans(
              'booking_request.spotahome_booking_payment_info.summary.our_fee'
            )}
            tooltipText={trans(
              'property_show.book_now_card.one_time_booking_fee.body'
            )}
            value={feeValue}
            valueWithoutOffer={feeCost?.valueWithoutOffer || null}
            currencyCode={currencyCode}
            dataTestValueWithoutOffer={
              feeCost?.valueWithoutOffer > 0 ? 'promo-code' : null
            }
          />
        ) : null}

        {restCosts?.length > 0
          ? restCosts.map(cost => {
              const unit = getUnit(cost.applyOn);
              const estimatedSubtitle = isEstimatedCost(cost.applyOn)
                ? trans('payment.timeline.estimated-cost')
                : null;
              const formattedBase =
                typeof cost.value === 'number'
                  ? formatPrice(cost.value, currencyCode)
                  : cost.value;
              const displayValue =
                unit && typeof cost.value === 'number'
                  ? trans(`price.per.${unit}`, { price: formattedBase })
                  : formattedBase;

              return (
                <DetailRow
                  key={cost.type}
                  label={
                    cost.name ??
                    trans(`payment.timeline.cost.label.${cost.type}`)
                  }
                  tooltipText={cost.description || null}
                  value={displayValue}
                  compareValueForStrike={
                    typeof cost.value === 'number' ? cost.value : null
                  }
                  valueWithoutOffer={cost.valueWithoutOffer}
                  currencyCode={currencyCode}
                  valueSubtitle={estimatedSubtitle}
                />
              );
            })
          : null}
      </div>

      <hr className={styles['on-booking-closed-card__divider']} />

      <div className={styles['on-booking-closed-card__total-row']}>
        <div className={styles['on-booking-closed-card__total-main']}>
          <div className={styles['on-booking-closed-card__total-left']}>
            <span className={styles['on-booking-closed-card__total-label']}>
              {trans('property_show.book_now_card.postpaid.total')}
            </span>
          </div>
          <div className={styles['on-booking-closed-card__total-value-column']}>
            <span className={styles['on-booking-closed-card__total-amount']}>
              {totalValue}
            </span>
          </div>
        </div>
        <span className={styles['on-booking-closed-card__total-caption']}>
          {trans('payment.details.paid.to.spotahome')}
        </span>
      </div>
    </div>
  );
};

OnBookingClosed.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  pricing: PropTypes.shape({}).isRequired,
  costs: ListingCostsShape,
  downPaymentTimeInterval: PropTypes.shape({
    type: PropTypes.oneOf(['notDefined', 'monthly', 'weekly']),
    value: PropTypes.number
  }),
  isInstantBooking: PropTypes.bool
};

export default OnBookingClosed;
